
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const milestones = [
  {
    year: '2009',
    title: 'Founded in San Francisco',
    description: 'TechVision launched with a 5-person team and a vision to democratize enterprise technology for growing businesses.',
    icon: 'ri-rocket-line',
  },
  {
    year: '2012',
    title: 'Global Expansion',
    description: 'Opened offices in London and Singapore, extending our reach to serve clients across three continents.',
    icon: 'ri-global-line',
  },
  {
    year: '2016',
    title: 'AI Innovation Lab',
    description: 'Launched a dedicated R&D center focused on artificial intelligence, machine learning, and predictive analytics.',
    icon: 'ri-brain-line',
  },
  {
    year: '2019',
    title: 'Cloud Excellence Award',
    description: 'Recognized by Gartner as a leading cloud solutions provider with multiple industry certifications.',
    icon: 'ri-award-line',
  },
  {
    year: '2022',
    title: '500+ Projects Delivered',
    description: 'Crossed the milestone of 500 successful enterprise projects spanning healthcare, finance, and retail.',
    icon: 'ri-flag-line',
  },
  {
    year: '2024',
    title: 'Next-Gen Platform Launch',
    description: 'Released our proprietary AI-powered platform for automated infrastructure management and optimization.',
    icon: 'ri-sparkling-2-line',
  },
];

export default function Timeline() {
  const header = useScrollAnimation();

  return (
    <section className="py-28 px-8 bg-[#1A2332]">
      <div className="max-w-[1400px] mx-auto">
        <div
          ref={header.ref}
          className={`text-center mb-20 transition-all duration-700 ${
            header.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="text-xs font-semibold text-[#0066CC] uppercase tracking-[0.2em] mb-4 block">Our Journey</span>
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">Milestones That Define Us</h2>
          <p className="text-base text-gray-400 max-w-xl mx-auto">
            From a small startup to a global technology leader — the key moments that shaped who we are.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {milestones.map((milestone, index) => (
            <TimelineCard key={index} milestone={milestone} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TimelineCard({ milestone, index }: { milestone: typeof milestones[0]; index: number }) {
  const card = useScrollAnimation({ rootMargin: '0px 0px -30px 0px' });

  const delayClasses = [
    'animation-delay-100',
    'animation-delay-200',
    'animation-delay-300',
    'animation-delay-400',
    'animation-delay-500',
    'animation-delay-600',
  ];

  return (
    <div
      ref={card.ref}
      className={`group bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 hover:border-[#0066CC]/30 transition-all duration-500 cursor-pointer ${
        card.isVisible
          ? `animate-fade-in-up ${delayClasses[index]}`
          : 'opacity-0-start'
      }`}
    >
      <div className="flex items-center justify-between mb-6">
        <span className="text-3xl font-bold text-[#0066CC]">{milestone.year}</span>
        <div className="w-10 h-10 rounded-xl bg-[#0066CC]/10 flex items-center justify-center group-hover:bg-[#0066CC]/20 transition-colors duration-300">
          <i className={`${milestone.icon} text-lg text-[#0066CC]`}></i>
        </div>
      </div>
      <h3 className="text-lg font-bold text-white mb-3">{milestone.title}</h3>
      <p className="text-sm text-gray-400 leading-relaxed">{milestone.description}</p>
    </div>
  );
}
